import {CalendarDays, Coins, CheckCircle2, Clock, AlertCircle, Sparkles, Eye, Loader2} from "lucide-react";
import {FC} from "react";
import {Button} from '@/components/ui/Button/index'
import {Link} from "react-router-dom";
import {PiTrashBold} from "react-icons/pi";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip";

interface ShredItemProps {
    hideHeader?: boolean;
    hideTowProgress?: boolean;
    id: string;
    title: string;
    startDate: string;
    progress: number;
    credit: string;
    generationTime: string;
    model: boolean
    project: string;
    status: 'CREATED' | 'GENERATING_SPECS' | 'SPECS_GENERATED' | 'PROMPT_VALIDATED' | 'PLAN_GENERATED' | 'IN_GENERATION' | 'RESUMING_GENERATION' | 'DONE' | 'FAILED' | 'STOPPING' | 'STOPPED';
    step?: string;
    summary?: string | null;
    isGeneratingSpecs?: boolean;
    isAnyShredGenerating?: boolean;
    isModelReady?: boolean;
    isModelValidated?: boolean; // Add this line
    onGenerateSpecs?: () => void;
    onExplore?: () => void;

    onDelete(shredId: string): void
}

const ShredItem: FC<ShredItemProps> = ({
                                           hideHeader,
                                           hideTowProgress,
                                           id,
                                           title,
                                           startDate,
                                           progress,
                                           credit,
                                           generationTime,
                                           model,
                                           project,
                                           step,
                                           status,
                                           isAnyShredGenerating = false,
                                           isModelReady = true,
                                           isModelValidated = true, // Add this line with default value
                                           onGenerateSpecs,
                                           onDelete
                                       }) => {

    const badges = [
        {
            title: "Start date",
            subTitle: startDate,
            icon: <CalendarDays className="w-4 h-4"/>,
            class: "bg-slate-50 text-slate-600 border-slate-200",
        },
        {
            title: "Credits Used",
            subTitle: credit,
            icon: <Coins className="w-4 h-4"/>,
            class: "bg-emerald-50 text-emerald-600 border-emerald-200",
        },
        {
            title: "Generation",
            subTitle: generationTime,
            icon: <CalendarDays className="w-4 h-4"/>,
            class: "bg-blue-50 text-blue-600 border-blue-200",
        },
    ];

    // UPDATED: Simple button logic based on your rules
    // UPDATED: Enhanced button logic to handle advanced/unknown states
    const shouldShowExploreButton = () => {

        if (step !== 'INITIALISING') {
            return true;
        }

        // Known states that should still show Generate Specs button
        const generateSpecsStates = ['CREATED', 'FAILED'];

        // Known states that should show Explore button
        const exploreStates = ['SPECS_GENERATED', 'DONE'];

        // For generating state, let the existing overlay handle it
        if (status === 'GENERATING_SPECS') {
            return false;
        }

        // If it's in a known "generate specs" state, show generate button
        if (generateSpecsStates.includes(status)) {
            return false;
        }

        // If it's in a known "explore" state, show explore button
        if (exploreStates.includes(status)) {
            return true;
        }

        // For any unknown/advanced states (PROMPT_VALIDATED, PLAN_GENERATED, etc.)
        // show explore button - this handles your new requirement
        return true;
    };

    const shouldDisableGenerateButton = () => {
        // Disable when currently generating specs OR when any other shred is generating
        if (status === 'GENERATING_SPECS' || isAnyShredGenerating) {
            return true;
        }

        // For feature shreds (non-model), disable if model is not ready OR not validated
        if (!model && (!isModelReady || !isModelValidated)) {
            return true;
        }

        return false;
    };

    // UPDATED: Status logic to show generating specs status
    const getStatus = () => {
        if (status === 'GENERATING_SPECS') {
            return {
                label: "Generating Specs",
                icon: <Loader2 className="w-4 h-4 animate-spin"/>,
                style: "bg-blue-50 text-blue-700 border border-blue-200",
                dotStyle: "bg-blue-500 animate-pulse"
            };
        } else if (status === 'SPECS_GENERATED' || status === 'DONE') {
            return {
                label: "Specs Ready",
                icon: <CheckCircle2 className="w-4 h-4"/>,
                style: "bg-green-50 text-green-700 border border-green-200",
                dotStyle: "bg-green-500"
            };
        } else if (progress === 100) {
            return {
                label: "Finished",
                icon: <CheckCircle2 className="w-4 h-4"/>,
                style: "bg-emerald-50 text-emerald-700 border border-emerald-200",
                dotStyle: "bg-emerald-500"
            };
        } else if (progress > 0 && progress < 100) {
            return {
                label: "In Progress",
                icon: <Clock className="w-4 h-4"/>,
                style: "bg-amber-50 text-amber-700 border border-amber-200",
                dotStyle: "bg-amber-500"
            };
        } else {
            return {
                label: "Pending",
                icon: <AlertCircle className="w-4 h-4"/>,
                style: "bg-slate-50 text-slate-600 border border-slate-200",
                dotStyle: "bg-slate-400"
            };
        }
    };

    const statusIndicator = getStatus();

    // Build the correct URL for the shred details page
    const shredUrl = `/architecture/project/${project}/shred/${id}`;

    // UPDATED: Get button disabled reason for tooltip
    const getDisabledReason = () => {
        if (shouldShowExploreButton()) return null;
        if (status === 'GENERATING_SPECS') return "Currently generating specifications...";
        if (isAnyShredGenerating) return "Another shred is currently generating. Please wait.";
        if (!model && !isModelReady) return "Generate Data Model specifications first";
        if (!model && !isModelValidated) return "Validate Data Model first before generating feature specs";
        return null;
    };

    const disabledReason = getDisabledReason();

    return (
        <div className="relative">
            <div
                data-tour={`shred-card-${id}`}
                className={`relative overflow-hidden rounded-2xl min-h-[300px] col-span-12 lg:col-span-6 id-${id} 
                    ${model
                    ? "bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 border border-indigo-200"
                    : "bg-white border border-slate-200"
                } 
                    flex flex-col shadow-sm hover:shadow-md transition-all duration-300 hover:border-slate-300`}
            >
                {/* Subtle gradient overlay for model cards */}
                {model && (
                    <div
                        className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5 pointer-events-none"/>
                )}

                {/* Status Indicator - Top Right */}
                <div className="absolute top-4 right-4 z-10">
                    <div
                        className={`inline-flex items-center space-x-2 px-3 py-1.5 rounded-full text-xs font-medium ${statusIndicator.style}`}>
                        <div className={`w-2 h-2 rounded-full ${statusIndicator.dotStyle}`}/>
                        {statusIndicator.icon}
                        <span>{statusIndicator.label}</span>
                    </div>
                </div>

                <div className="relative z-10 p-6 flex flex-col h-full">
                    {/* Top Section - Shred Title */}
                    {!hideHeader && (
                        <div className="mb-6">
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger className="block">
                                        <h1 className="text-xl font-semibold text-center text-slate-800 truncate max-w-full leading-tight">
                                            {title}
                                        </h1>
                                    </TooltipTrigger>
                                    <TooltipContent className="w-80">
                                        <p className="text-sm text-slate-700">{title}</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                    )}

                    {/* Middle Section - Shred Information */}
                    <div className="flex-1 grid grid-cols-12 gap-4 mb-6">
                        {/* User Profile Section */}
                        <div className="col-span-12 lg:col-span-3">
                            <div
                                className="bg-white/60 backdrop-blur-sm border border-slate-200 rounded-xl flex justify-center items-center py-4 h-full hover:bg-white/80 transition-colors">
                                <img
                                    className="rounded-full border-2 border-white shadow-sm"
                                    src={"/images/user-default.jpg"}
                                    alt="Shred Creator"
                                    width={56}
                                    height={56}
                                />
                            </div>
                        </div>

                        {/* Information and Progress Section */}
                        <div className="col-span-12 lg:col-span-9 flex flex-col gap-3">
                            {/* First Row - Two Badges */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {badges.slice(0, 2).map((badge, index) => (
                                    <div key={index}
                                         className={`flex items-center gap-3 p-3 rounded-xl border ${badge.class} hover:shadow-sm transition-all`}>
                                        <div className="p-1.5 bg-white/80 rounded-lg shadow-sm">
                                            {badge.icon}
                                        </div>
                                        <div>
                                            <h6 className="text-xs font-medium opacity-75 mb-0.5">
                                                {badge.title}
                                            </h6>
                                            <span className="text-sm font-semibold">
                                                {badge.subTitle}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Second Row - Third Badge and Progress */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {/* Third Badge */}
                                <div
                                    className={`flex items-center gap-3 p-3 rounded-xl border ${badges[2].class} hover:shadow-sm transition-all`}>
                                    <div className="p-1.5 bg-white/80 rounded-lg shadow-sm">
                                        {badges[2].icon}
                                    </div>
                                    <div>
                                        <h6 className="text-xs font-medium opacity-75 mb-0.5">
                                            {badges[2].title}
                                        </h6>
                                        <span className="text-sm font-semibold">
                                            {badges[2].subTitle}
                                        </span>
                                    </div>
                                </div>

                                {/* AI Generated Progress */}
                                {!hideTowProgress && (
                                    <div
                                        className="p-3 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl hover:shadow-sm transition-all">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="text-xs font-medium text-indigo-700">
                                                AI Generated
                                            </span>
                                            <div className="px-2 py-1 bg-white/80 rounded-md">
                                                <span className="text-xs font-bold text-indigo-800">
                                                    {progress}%
                                                </span>
                                            </div>
                                        </div>
                                        <div className="relative">
                                            <div className="h-2 bg-white/60 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-500 ease-out"
                                                    style={{width: `${progress}%`}}
                                                />
                                            </div>
                                            {progress > 0 && progress < 100 && (
                                                <div
                                                    className="absolute inset-0 bg-gradient-to-r from-indigo-500/20 to-purple-500/20 rounded-full animate-pulse"/>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Separator Line */}
                    <div className="h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent mb-6"/>

                    {/* Bottom Section - Action Buttons */}
                    <div className="grid grid-cols-2 gap-3 mt-auto">
                        <Button
                            loading={false}
                            variant='default'
                            size='md'
                            className='w-full border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 transition-colors'
                            icon={<PiTrashBold/>}
                            onClick={() => onDelete(id)}
                        >
                            Delete
                        </Button>

                        {/* UPDATED: Simple button logic based on your rules */}
                        {shouldShowExploreButton() ? (
                            // Show Explore button when status is SPECS_GENERATED or DONE
                            <Link
                                data-tour={`explore-${id}`}
                                to={shredUrl}
                                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white text-sm py-3 px-4 rounded-xl font-medium flex items-center justify-center w-full transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-[1.02] no-underline"
                            >
                                <Eye className="inline mr-2 h-4 w-4"/>
                                Explore
                            </Link>
                        ) : (
                            // Show Generate Specs button for CREATED status
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <button
                                            data-tour={`generate-specs-${id}`}
                                            onClick={onGenerateSpecs}
                                            disabled={shouldDisableGenerateButton()}
                                            className={`text-white text-sm py-3 px-4 rounded-xl font-medium flex items-center justify-center w-full transition-all duration-200 shadow-sm hover:shadow-md transform ${
                                                shouldDisableGenerateButton()
                                                    ? 'bg-gray-400 cursor-not-allowed'
                                                    : 'bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 hover:scale-[1.02]'
                                            }`}
                                        >
                                            <Sparkles className="inline mr-2 h-4 w-4"/>
                                            Generate Specs
                                        </button>
                                    </TooltipTrigger>
                                    {disabledReason && (
                                        <TooltipContent>
                                            <p className="text-sm">{disabledReason}</p>
                                        </TooltipContent>
                                    )}
                                </Tooltip>
                            </TooltipProvider>
                        )}
                    </div>
                </div>
            </div>

            {/* Simple dark overlay for generating specs */}
            {status === 'GENERATING_SPECS' && (
                <div
                    className="absolute inset-0 bg-black/60 backdrop-blur-sm rounded-2xl flex items-center justify-center z-20">
                    <div className="text-center text-white">
                        <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4"/>
                        <h3 className="text-lg font-semibold mb-2">Generating Specifications</h3>
                        <p className="text-sm opacity-90">This may take up to 4 minutes...</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ShredItem;